import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { authApi } from '../services/apiAuth';
import { canAccessPage, firstAllowedPath, pageKeyFromPath, normalizePortalPath } from '../constants/adminPages';

const EmailIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16 12a4 4 0 10-8 0 4 4 0 008 0zm0 0v1.5a2.5 2.5 0 005 0V12a9 9 0 10-9 9m4.5-1.206a8.959 8.959 0 01-4.5 1.207" />
    </svg>
);

const LockIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
    </svg>
);

function AuthSection() {
    const { login } = useAuth();

    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [remember, setRemember] = useState(true);
    const [showPassword, setShowPassword] = useState(false);
    const [error, setError] = useState('');
    const [isSubmitting, setIsSubmitting] = useState(false);

    useEffect(() => {
        const saved = authApi.getRememberedEmail();
        if (saved) {
            setEmail(saved);
            setRemember(true);
        }
    }, []);

    const handleLogin = async (e) => {
        e.preventDefault();
        setError('');
        setIsSubmitting(true);

        try {
            const result = await login(email, password, remember);
            const redirect = normalizePortalPath(new URLSearchParams(window.location.search).get('redirect') || '');
            const fallback = firstAllowedPath(result?.user);
            const redirectKey = redirect ? pageKeyFromPath(redirect) : '';
            const target = redirect && canAccessPage(result?.user, redirectKey || redirect)
              ? redirect
              : fallback;
            setTimeout(() => {
              window.location.href = target;
            }, 50);
        } catch (err) {
            const hasResponse = !!err.response;
            const status = err.response?.status;
            const errors = err.response?.data?.errors;
            const backendMsg = err.response?.data?.message;

            if (!hasResponse) {
                const live = typeof window !== 'undefined' &&
                    window.location.hostname !== 'localhost' &&
                    window.location.hostname !== '127.0.0.1';
                setError(
                    live
                        ? 'Cannot reach the server API. Please try again in a moment.'
                        : (
                            '⚠️ BACKEND SERVER IS NOT RUNNING (port 8000 closed).\n' +
                            'Please go to project folder → double-click "01_SETUP_BACKEND_FIRST.cmd" first to install dependencies & seed DB,\n' +
                            'then double-click "02_START_SERVERS.cmd" to start the Laravel API. After backend starts, try login again.\n' +
                            '(Developer: the API request failed with no HTTP response — likely php artisan serve not running.)'
                        )
                );
            } else if (status === 429 || /too many/i.test(String(backendMsg || ''))) {
                setError('Too many login attempts. Please wait a minute and try again.');
            } else if (status === 401 || status === 422) {
                const firstErr = errors ? Object.values(errors).flat()[0] : '';
                const looksLikeEmailFormat = /valid email|email field/i.test(String(firstErr || ''));
                setError(looksLikeEmailFormat ? String(firstErr) : 'Wrong email or password.');
            } else if (status === 403) {
                setError(backendMsg || 'Your account is inactive. Please contact the administrator.');
            } else if (status >= 500) {
                setError(`Server error (${status}): ${backendMsg || 'Please check the Laravel log files.'}`);
            } else {
                setError(backendMsg || 'Login failed.');
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen flex flex-col lg:flex-row bg-white">
            <div className="relative lg:w-1/2 flex flex-col items-center justify-center p-8 py-12 bg-slate-50 text-center overflow-hidden">
                <div className="absolute -top-16 -left-16 w-72 h-72 bg-red-100 rounded-full opacity-50"></div>
                <div className="absolute -bottom-24 -right-10 w-96 h-96 border-4 border-red-100 rounded-full opacity-50"></div>
                <div className="relative z-10 flex-grow flex flex-col items-center justify-center">
                    <h2 className="text-2xl md:text-3xl font-light text-gray-600 mb-4">Welcome to</h2>
                    <img src="/Flare Logo.png" alt="Flare Magazine Logo" className="h-32 md:h-44 w-auto mx-auto" />
                </div>
                <div className="relative z-10 w-full mt-12 pt-8 border-t border-slate-200">
                    <p className="text-sm font-semibold text-gray-500 mb-4">Also a part of the Mix FM Network</p>
                    <div className="flex justify-center items-center gap-x-4 sm:gap-x-6">
                        <img src="/Mix FM Logo 99.png" alt="Mix FM 99" className="h-16 w-auto" />
                        <img src="/Mix FM Logo 100.png" alt="Mix FM 100" className="h-16 w-auto" />
                        <img src="/Mix FM Logo 96.png" alt="Mix FM 96" className="h-16 w-auto" />
                    </div>
                </div>
            </div>

            <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
                <div className="w-full max-w-sm">
                    <h2 className="text-3xl font-bold text-gray-900 mb-2">Log in to your <span style={{ color: '#c4151c' }}>Flare Account</span></h2>
                    <p className="text-gray-500 mb-8">Welcome back! Please enter your details.</p>
                    <form onSubmit={handleLogin} className="space-y-6">
                        <div>
                            <label htmlFor="email" className="block text-sm font-medium text-gray-700">Email</label>
                            <div className="relative mt-1">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <EmailIcon />
                                </div>
                                <input
                                    type="email"
                                    id="email"
                                    value={email}
                                    onChange={(e) => setEmail(e.target.value)}
                                    required
                                    placeholder="Enter your email"
                                    className="block w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#c4151c] focus:border-transparent"
                                />
                            </div>
                        </div>

                        <div>
                            <label htmlFor="password" className="block text-sm font-medium text-gray-700">Password</label>
                            <div className="relative mt-1">
                                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                                    <LockIcon />
                                </div>
                                <input
                                    type={showPassword ? "text" : "password"}
                                    id="password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    required
                                    placeholder="Enter your password"
                                    className="block w-full pl-10 pr-16 py-3 border border-gray-300 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#c4151c] focus:border-transparent"
                                />
                                <button
                                    type="button"
                                    onClick={() => setShowPassword(!showPassword)}
                                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-sm font-semibold text-gray-600"
                                >
                                    {showPassword ? 'Hide' : 'Show'}
                                </button>
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <label htmlFor="remember" className="flex items-center gap-2 cursor-pointer select-none">
                                <input
                                    type="checkbox"
                                    id="remember"
                                    checked={remember}
                                    onChange={(e) => setRemember(e.target.checked)}
                                    className="w-4 h-4 rounded border-gray-300 text-[#c4151c] focus:ring-[#c4151c]"
                                />
                                <span className="text-sm font-medium text-gray-700">Remember me</span>
                            </label>
                        </div>

                        {error && (
                            <div className="p-4 rounded-lg bg-red-100 text-red-800 text-left text-lg font-bold leading-snug whitespace-pre-line">
                                {error}
                            </div>
                        )}
                        <button
                            type="submit"
                            className="w-full py-3 px-6 rounded-lg shadow-lg text-lg font-bold text-white transition-all transform hover:scale-105"
                            style={{ backgroundColor: isSubmitting ? '#a11217' : '#c4151c' }}
                            disabled={isSubmitting}
                        >
                            {isSubmitting ? 'Logging in...' : 'Log In'}
                        </button>
                    </form>
                </div>
            </div>
        </div>
    );
}

export default AuthSection;
